import { useTranslation } from "react-i18next"

import type { TFunction } from "i18next"

import { CheckCircle2, Clock, Loader2, PauseCircle, XCircle } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"

import { WorkflowRunOutput } from "./workflow-run-output"
import {
  type FlowRun,
  type FlowRunDiagnosticEvent,
  type PendingInterruptField,
  useResumeWorkflowRun,
  useWorkflowRuns,
} from "../hooks/use-workflow-runs"
import { type DenialSplit, getRunDenial } from "../utils/workflow-run-denial-utils"

// A file field is recognised by its type prefix (e.g. "file", "file_0"). File
// inputs ride the upload pipeline and can't be re-supplied on resume yet.
function isFileField(field: PendingInterruptField) {
  return field.type.toLowerCase().startsWith("file")
}

type WorkflowRunsDialogProps = {
  workflowId: string
  workflowTitle?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

function getRunStatusLabel(run: FlowRun, denied: boolean) {
  return denied ? "Denied" : run.status
}

function StatusIcon({ run, denied }: { run: FlowRun; denied: boolean }) {
  if (denied) return <XCircle className="text-warning-ink size-4" />
  if (run.status === "succeeded") return <CheckCircle2 className="text-success size-4" />
  if (run.status === "failed") return <XCircle className="text-destructive size-4" />
  if (run.status === "paused") return <PauseCircle className="text-destructive size-4" />
  if (run.status === "queued") return <Clock className="text-muted-foreground size-4" />
  return <Loader2 className="text-warning-ink size-4 animate-spin" />
}

function formatDiagnosticDate(value: string, language?: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const date = new Date(`${value}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(language, {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(date)
}

function formatDiagnosticFreshness(value: string, t: TFunction, language?: string) {
  const freshnessLabels: Partial<Record<string, [string, string]>> = {
    pd: ["runs.diagnostics.freshnessPastDay", "Last 24 hours"],
    pw: ["runs.diagnostics.freshnessPastWeek", "Last week"],
    pm: ["runs.diagnostics.freshnessPastMonth", "Last month"],
    py: ["runs.diagnostics.freshnessPastYear", "Last year"],
  }
  const preset = freshnessLabels[value]
  if (preset) return t(preset[0], preset[1])

  const dateRange = /^(\d{4}-\d{2}-\d{2})to(\d{4}-\d{2}-\d{2})$/.exec(value)
  if (dateRange) {
    return `${formatDiagnosticDate(dateRange[1], language)} – ${formatDiagnosticDate(dateRange[2], language)}`
  }
  return value
}

function diagnosticDetailSummary(event: FlowRunDiagnosticEvent, t: TFunction, language?: string) {
  const details = event.details
  if (!details) return ""

  const parts: string[] = []
  if (details.requested_model)
    parts.push(`${t("runs.diagnostics.requested", "Requested")}: ${details.requested_model}`)
  if (details.effective_model)
    parts.push(`${t("runs.diagnostics.model", "Model")}: ${details.effective_model}`)
  if (
    details.requested_reasoning_effort &&
    details.requested_reasoning_effort !== details.reasoning_effort
  ) {
    parts.push(
      `${t("runs.diagnostics.requestedReasoning", "Requested reasoning")}: ${details.requested_reasoning_effort}`
    )
    if (details.reasoning_effort) {
      parts.push(
        `${t("runs.diagnostics.effectiveReasoning", "Effective reasoning")}: ${details.reasoning_effort}`
      )
    }
  } else if (details.reasoning_effort) {
    parts.push(`${t("runs.diagnostics.reasoning", "Reasoning")}: ${details.reasoning_effort}`)
  }
  if (details.result_count !== undefined)
    parts.push(`${t("runs.diagnostics.results", "Results")}: ${details.result_count}`)
  if (typeof details.freshness === "string") {
    parts.push(
      `${t("runs.diagnostics.freshness", "Freshness")}: ${formatDiagnosticFreshness(details.freshness, t, language)}`
    )
  }
  if (typeof details.date_from === "string") {
    parts.push(
      `${t("runs.diagnostics.dateFrom", "From")}: ${formatDiagnosticDate(details.date_from, language)}`
    )
  }
  if (typeof details.date_to === "string") {
    parts.push(
      `${t("runs.diagnostics.dateTo", "To")}: ${formatDiagnosticDate(details.date_to, language)}`
    )
  }
  if (Array.isArray(details.publication_dates)) {
    parts.push(
      `${t("runs.diagnostics.datedSources", "Dated sources")}: ${details.publication_dates.length}`
    )
  }
  if (details.cache_hit) parts.push(t("runs.diagnostics.cached", "Cached"))
  if (details.failed_url_count)
    parts.push(`${t("runs.diagnostics.failedPages", "Failed pages")}: ${details.failed_url_count}`)
  if (details.partial) parts.push(t("runs.diagnostics.partial", "Partial"))
  if (details.from_model && details.to_model) {
    parts.push(
      `${t("runs.diagnostics.fallback", "Fallback")}: ${details.from_model} → ${details.to_model}`
    )
  }
  if (details.iteration !== undefined) {
    parts.push(
      details.max_iterations !== undefined
        ? `${t("runs.diagnostics.iteration", "Iteration")}: ${details.iteration}/${details.max_iterations}`
        : `${t("runs.diagnostics.iteration", "Iteration")}: ${details.iteration}`
    )
  }
  if (details.error_code)
    parts.push(`${t("runs.diagnostics.error", "Error")}: ${details.error_code}`)
  if (details.timed_out) parts.push(t("runs.diagnostics.timedOut", "Timed out"))
  if (details.skipped) parts.push(t("runs.diagnostics.skipped", "Skipped"))
  return parts.join(" · ")
}

function diagnosticEventLabel(event: FlowRunDiagnosticEvent, t: TFunction) {
  if (event.type === "web_search") {
    const webSearchLabels: Partial<Record<string, [string, string]>> = {
      started: ["runs.diagnostics.webSearchStarted", "Web search started"],
      completed: ["runs.diagnostics.webSearchCompleted", "Web search completed"],
      error: ["runs.diagnostics.webSearchFailed", "Web search failed"],
      timeout: ["runs.diagnostics.webSearchTimedOut", "Web search timed out"],
      cancelled: ["runs.diagnostics.webSearchCancelled", "Web search cancelled"],
    }
    const label = webSearchLabels[String(event.details?.status ?? "")]
    return label
      ? t(label[0], label[1])
      : t("runs.diagnostics.webSearchActivity", "Web search activity")
  }
  if (event.details?.timed_out) return t("runs.diagnostics.toolTimedOut", "Tool timed out")
  if (event.details?.from_model && event.details.to_model)
    return t("runs.diagnostics.modelFallbackEvent", "Model fallback")
  if (event.details?.tool === "web_search")
    return t("runs.diagnostics.webSearchActivity", "Web search activity")
  if (event.details?.tool) return t("runs.diagnostics.toolActivity", "Tool activity")
  return t("runs.diagnostics.flowActivity", "Flow activity")
}

function formatDiagnosticTimestamp(value: string, language?: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(language, {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(date)
}

function RunDiagnostics({ run }: { run: FlowRun }) {
  const { i18n, t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  const events = run.diagnostics?.events ?? []
  const modelResolutions = Object.entries(run.diagnostics?.model_resolutions ?? {})
  const diagnosticCount = modelResolutions.length + events.length
  if (diagnosticCount === 0) return null

  return (
    <details className="mt-1.5 rounded border px-2 py-1">
      <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-[11px]">
        {t("runs.showDiagnostics", "Show run diagnostics")} ({diagnosticCount})
      </summary>
      <ol className="mt-1 max-h-48 space-y-1 overflow-auto">
        {modelResolutions.map(([nodeId, resolution]) => (
          <li key={`model-${nodeId}`} className="border-l pl-2 text-[11px]">
            <p className="text-foreground">
              {t("runs.diagnostics.modelConfigured", "Model configured for")} {nodeId}
            </p>
            <p className="text-muted-foreground break-words">
              {[
                resolution.requested_model &&
                  `${t("runs.diagnostics.requested", "Requested")}: ${resolution.requested_model}`,
                resolution.effective_model &&
                  `${t("runs.diagnostics.model", "Model")}: ${resolution.effective_model}`,
                resolution.requested_reasoning_effort &&
                  resolution.requested_reasoning_effort !== resolution.reasoning_effort &&
                  `${t("runs.diagnostics.requestedReasoning", "Requested reasoning")}: ${resolution.requested_reasoning_effort}`,
                resolution.reasoning_effort &&
                  `${t(
                    resolution.requested_reasoning_effort &&
                      resolution.requested_reasoning_effort !== resolution.reasoning_effort
                      ? "runs.diagnostics.effectiveReasoning"
                      : "runs.diagnostics.reasoning",
                    resolution.requested_reasoning_effort &&
                      resolution.requested_reasoning_effort !== resolution.reasoning_effort
                      ? "Effective reasoning"
                      : "Reasoning"
                  )}: ${resolution.reasoning_effort}`,
                resolution.api_surface &&
                  `${t("runs.diagnostics.api", "API")}: ${resolution.api_surface}`,
                resolution.legacy_model_default &&
                  t("runs.diagnostics.legacyDefault", "Legacy automatic model selection"),
                resolution.configuration_fallback &&
                  t("runs.diagnostics.configurationFallback", "Configuration fallback"),
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </li>
        ))}
        {events.map((event, index) => {
          const detailSummary = diagnosticDetailSummary(event, t, i18n.resolvedLanguage)
          return (
            <li key={`${event.at}-${event.type}-${index}`} className="border-l pl-2 text-[11px]">
              <div className="flex flex-wrap gap-x-2">
                <span className="text-foreground">{diagnosticEventLabel(event, t)}</span>
                <span className="text-muted-foreground">
                  {formatDiagnosticTimestamp(event.at, i18n.resolvedLanguage)}
                </span>
              </div>
              {detailSummary && (
                <p className="text-muted-foreground break-words">{detailSummary}</p>
              )}
            </li>
          )
        })}
      </ol>
    </details>
  )
}

/** Body of a single run row: approval prompt + actions when paused, else error/output. */
function RunRowBody({
  run,
  denial,
  onDecide,
  onSubmitInput,
  decidePending,
}: {
  run: FlowRun
  denial: DenialSplit
  onDecide: (runId: string, decision: "approve" | "deny") => void
  onSubmitInput: (runId: string, decision: Record<string, unknown>) => void
  decidePending: boolean
}) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })

  if (run.status === "paused" && run.resumable !== false) {
    if (run.pending_interrupt?.type === "user_input_required") {
      const prompt =
        run.pending_interrupt.prompt || t("runs.inputRequired", "Input required to continue.")
      const fields = run.pending_interrupt.fields
      const textFields = (fields ?? []).filter((field) => !isFileField(field))
      const fileFields = (fields ?? []).filter((field) => isFileField(field))

      // Multi-field pause: render one control per TEXT field and post keyed
      // `field_responses` (mirrors WorkflowRunResumeCard). The legacy single
      // textarea below only fills the first field and re-pauses for the rest.
      if (fields && fields.length > 0) {
        return (
          <form
            className="border-warning-ink mt-1 space-y-2 border-l-2 pl-2"
            onSubmit={(event) => {
              event.preventDefault()
              const formData = new FormData(event.currentTarget)
              const fieldResponses: Record<string, string> = {}
              for (const field of textFields) {
                fieldResponses[field.key] = String(formData.get(field.key) || "").trim()
              }
              onSubmitInput(run.id, { field_responses: fieldResponses })
            }}
          >
            <p className="text-foreground">{prompt}</p>
            {textFields.map((field) => (
              <div key={field.key} className="space-y-1">
                <label
                  className="text-foreground block text-[13px]"
                  htmlFor={`flow-run-input-${run.id}-${field.key}`}
                >
                  {field.label || field.key}
                  {field.required && <span className="text-warning-ink"> *</span>}
                </label>
                <Input
                  className="h-7 text-xs"
                  disabled={decidePending}
                  id={`flow-run-input-${run.id}-${field.key}`}
                  name={field.key}
                  required={field.required}
                />
              </div>
            ))}
            {fileFields.length > 0 && (
              <p className="text-muted-foreground rounded border border-dashed px-2 py-1 text-[11px]">
                {t("runs.fileResumeUnsupported", "File inputs can't be provided on resume yet")}
                {` (${fileFields.map((field) => field.label || field.key).join(", ")}).`}
              </p>
            )}
            <Button
              className="h-6 px-2 text-[11px]"
              // A file-only pause has no text controls — don't let the user complete blank.
              disabled={decidePending || textFields.length === 0}
              size="sm"
              type="submit"
            >
              {t("runs.continue", "Continue")}
            </Button>
          </form>
        )
      }

      return (
        <form
          className="border-warning-ink mt-1 border-l-2 pl-2"
          onSubmit={(event) => {
            event.preventDefault()
            const form = event.currentTarget
            const formData = new FormData(form)
            const text = String(formData.get("text") || "").trim()
            if (text) {
              onSubmitInput(run.id, { text })
            }
          }}
        >
          <label className="text-foreground block" htmlFor={`flow-run-input-${run.id}`}>
            {prompt}
          </label>
          <Textarea
            required
            className="mt-1 min-h-20 text-xs"
            disabled={decidePending}
            id={`flow-run-input-${run.id}`}
            name="text"
          />
          <Button
            className="mt-1.5 h-6 px-2 text-[11px]"
            disabled={decidePending}
            size="sm"
            type="submit"
          >
            {t("runs.continue", "Continue")}
          </Button>
        </form>
      )
    }

    return (
      <div className="border-destructive mt-1 border-l-2 pl-2">
        <p className="text-foreground">
          {run.pending_interrupt?.prompt ||
            t("runs.awaitingApproval", "Awaiting your approval to continue.")}
        </p>
        {run.pending_interrupt?.preview && (
          <p className="text-muted-foreground mt-0.5 line-clamp-2 whitespace-pre-wrap">
            {run.pending_interrupt.preview}
          </p>
        )}
        <div className="mt-1.5 flex gap-2">
          <Button
            className="h-6 px-2 text-[11px]"
            disabled={decidePending}
            size="sm"
            onClick={() => onDecide(run.id, "approve")}
          >
            {t("runs.approve", "Approve")}
          </Button>
          <Button
            className="h-6 px-2 text-[11px]"
            disabled={decidePending}
            size="sm"
            variant="outline"
            onClick={() => onDecide(run.id, "deny")}
          >
            {t("runs.deny", "Deny")}
          </Button>
        </div>
      </div>
    )
  }

  if (run.error) {
    return <p className="text-destructive mt-1 line-clamp-2">{run.error}</p>
  }

  // The denial marker is split off (#2998). A denied run with nothing else in
  // its output shows only its reason rather than "(no output)".
  let output = denial.output
  if (!output && !denial.reasons.length) output = t("runs.noOutput", "(no output)")
  return <WorkflowRunOutput output={output} reasons={denial.reasons} />
}

export function WorkflowRunsDialog({
  workflowId,
  workflowTitle,
  open,
  onOpenChange,
}: WorkflowRunsDialogProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  const { data: runs = [], isLoading } = useWorkflowRuns(workflowId, open)
  const resume = useResumeWorkflowRun(workflowId)

  const decide = (runId: string, decision: "approve" | "deny") => {
    resume.mutate({ runId, decision: { decision } })
  }
  const submitInput = (runId: string, decision: Record<string, unknown>) => {
    resume.mutate({ runId, decision })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="size-4" />
            {t("runs.title", "Run history")}
            {workflowTitle ? ` — ${workflowTitle}` : ""}
          </DialogTitle>
          <DialogDescription>
            {t("runs.description", "Recent executions of this flow (manual, scheduled, webhook).")}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <p className="text-muted-foreground text-xs">{t("runs.loading", "Loading…")}</p>
        )}
        {!isLoading && runs.length === 0 && (
          <p className="text-muted-foreground text-xs">{t("runs.none", "No runs yet.")}</p>
        )}
        {!isLoading && runs.length > 0 && (
          <ul className="max-h-[60vh] divide-y overflow-y-auto">
            {runs.map((run) => {
              // Parsed once per row: the label, icon and body all need it.
              const denial = getRunDenial(run)
              return (
                <li key={run.id} className="flex items-start gap-3 py-2 text-xs">
                  <StatusIcon denied={denial.denied} run={run} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium capitalize">
                        {getRunStatusLabel(run, denial.denied)}
                      </span>
                      <span className="text-muted-foreground bg-muted rounded px-1.5 py-0.5 text-[10px]">
                        {run.trigger}
                      </span>
                      <span className="text-muted-foreground">{run.started_at}</span>
                    </div>
                    <RunRowBody
                      decidePending={resume.isPending}
                      denial={denial}
                      run={run}
                      onDecide={decide}
                      onSubmitInput={submitInput}
                    />
                    <RunDiagnostics run={run} />
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}
